import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { Button, CurrenciesUSD, Icon, Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { TransferBatchRepository } from '~/repositories/Transfer/TransferBatchRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { TransferBatch } from '~/typings/API';

const columns: TableColumn<TransferBatch>[] = [
  {
    header: 'Amount / Description',
    accessor: 'totalAmount',
    width: { min: 150, max: '4fr' },
    cell: ({ row, value }) => (
      <Table.AmountCell
        currencyList={CurrenciesUSD}
        fixedCurrencyCode={row.totalCurrency}
        value={value}
        note={row.description ?? undefined}
        isBold
        isColorized
      />
    ),
  },
  {
    header: 'Transfers',
    accessor: 'transferCount',
    width: { min: 100, max: '1fr' },
    cell: ({ value }) => <Table.TextCell value={value?.toString() ?? ''} placeholder="Transfer Count" />,
  },
  {
    header: 'Created',
    accessor: 'createdAt',
    width: { fixed: 80 },
    cell: Table.TimeCell,
  },
];

export const PageTransferBatchesOverview: React.FC = () => {
  const { currentPlatform } = useSessionStore();
  const navigate = useNavigate();
  const { cursor, onNext, setPageSize, ...pagination } = usePaginationState();

  const { data: transferBatchesData, isLoading: isTransferBatchesLoading } = useQuery({
    queryKey: ['transfers', 'batches', currentPlatform, cursor, pagination.pageSize],
    queryFn: async () =>
      TransferBatchRepository.getAll({
        limit: pagination.pageSize,
        startingAfter: cursor,
      }),
    gcTime: 0,
    staleTime: 0,
  });

  return (
    <>
      <Inner pt={0} px="var(--page-margin)">
        <Table<TransferBatch>
          columns={columns}
          data={transferBatchesData?.batches ?? []}
          pagination={{
            hasNext: transferBatchesData?.hasMore ?? false,
            onNext: () =>
              transferBatchesData
                ? onNext({ cursor: transferBatchesData?.batches[transferBatchesData?.batches.length - 1].id })
                : undefined,
            onPageSizeChange: setPageSize,
            ...pagination,
          }}
          isLoading={isTransferBatchesLoading}
          rowAction={(row) => (
            <Button
              onClick={(event: React.SyntheticEvent) => {
                event.stopPropagation();
                navigate(`${ROUTE.TRANSFERS}/batches/${row.id}`);
              }}
              icon={<Icon.ChevronRightLarge />}
              iconRight
              size="tiny"
              variant="secondary"
            >
              View Batch
            </Button>
          )}
          variant={TableVariant.List}
          onRowClick={(row) => {
            navigate(`${ROUTE.TRANSFERS}/batches/${row.id}`);
          }}
        />
      </Inner>
    </>
  );
};
